/**
 * Analytics Hooks
 * React Query hooks for fetching analytics data from Supabase
 */

import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { supabase } from '@/lib/supabase'
import {
  getDateRangeFromPreset,
  getOverviewStats,
  getDailyTraffic,
  getDailyRevenue,
  getTopPages,
  getBookingFunnel,
  getAdPerformance,
  getDeviceBreakdown,
  type DateRange,
  type DateRangePreset,
  type OverviewStats,
  type TrafficDataPoint,
  type RevenueDataPoint,
  type TopPage,
  type BookingFunnelData,
  type AdPerformanceData,
  type DeviceBreakdown,
} from '@/lib/analytics'

// Default refresh interval (1 minute for near real-time)
const REFRESH_INTERVAL = 60 * 1000

// Re-export getDateRangeFromPreset for convenience
export { getDateRangeFromPreset }

// Re-export types
export type {
  DateRange,
  DateRangePreset,
  OverviewStats,
  TrafficDataPoint,
  RevenueDataPoint,
  TopPage,
  BookingFunnelData,
  AdPerformanceData,
  DeviceBreakdown,
}

/**
 * Get the active account ID (handles impersonation)
 */
function useActiveAccountId(): string | null {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const activeAccount = isImpersonating ? impersonatedAccount : account
  return activeAccount?.id || null
}

/**
 * Hook for overview statistics
 */
export function useOverviewStats(dateRange: DateRange) {
  const accountId = useActiveAccountId()

  return useQuery<OverviewStats | null>({
    queryKey: [
      'analytics',
      'overview',
      accountId,
      dateRange.start.toISOString(),
      dateRange.end.toISOString(),
    ],
    queryFn: async () => {
      if (!accountId) return null
      return getOverviewStats(supabase, accountId, dateRange)
    },
    enabled: !!accountId,
    refetchInterval: REFRESH_INTERVAL,
    staleTime: 30 * 1000,
  })
}

/**
 * Hook for daily traffic data (charts)
 */
export function useTrafficData(dateRange: DateRange) {
  const accountId = useActiveAccountId()

  return useQuery<TrafficDataPoint[]>({
    queryKey: [
      'analytics',
      'traffic',
      accountId,
      dateRange.start.toISOString(),
      dateRange.end.toISOString(),
    ],
    queryFn: async () => {
      if (!accountId) return []
      return getDailyTraffic(supabase, accountId, dateRange)
    },
    enabled: !!accountId,
    refetchInterval: REFRESH_INTERVAL,
    staleTime: 30 * 1000,
  })
}

/**
 * Hook for daily revenue data (charts)
 */
export function useRevenueData(dateRange: DateRange) {
  const accountId = useActiveAccountId()

  return useQuery<RevenueDataPoint[]>({
    queryKey: [
      'analytics',
      'revenue',
      accountId,
      dateRange.start.toISOString(),
      dateRange.end.toISOString(),
    ],
    queryFn: async () => {
      if (!accountId) return []
      return getDailyRevenue(supabase, accountId, dateRange)
    },
    enabled: !!accountId,
    refetchInterval: REFRESH_INTERVAL,
    staleTime: 30 * 1000,
  })
}

/**
 * Hook for top pages
 */
export function useTopPages(dateRange: DateRange, limit: number = 10) {
  const accountId = useActiveAccountId()

  return useQuery<TopPage[]>({
    queryKey: [
      'analytics',
      'pages',
      accountId,
      dateRange.start.toISOString(),
      dateRange.end.toISOString(),
      limit,
    ],
    queryFn: async () => {
      if (!accountId) return []
      return getTopPages(supabase, accountId, dateRange, limit)
    },
    enabled: !!accountId,
    refetchInterval: REFRESH_INTERVAL,
    staleTime: 30 * 1000,
  })
}

/**
 * Hook for booking funnel
 */
export function useBookingFunnel(dateRange: DateRange) {
  const accountId = useActiveAccountId()

  return useQuery<BookingFunnelData | null>({
    queryKey: [
      'analytics',
      'funnel',
      accountId,
      dateRange.start.toISOString(),
      dateRange.end.toISOString(),
    ],
    queryFn: async () => {
      if (!accountId) return null
      return getBookingFunnel(supabase, accountId, dateRange)
    },
    enabled: !!accountId,
    refetchInterval: REFRESH_INTERVAL,
    staleTime: 30 * 1000,
  })
}

/**
 * Hook for ad performance (UTM attribution)
 */
export function useAdPerformance(dateRange: DateRange) {
  const accountId = useActiveAccountId()

  return useQuery<AdPerformanceData[]>({
    queryKey: [
      'analytics',
      'ads',
      accountId,
      dateRange.start.toISOString(),
      dateRange.end.toISOString(),
    ],
    queryFn: async () => {
      if (!accountId) return []
      return getAdPerformance(supabase, accountId, dateRange)
    },
    enabled: !!accountId,
    refetchInterval: REFRESH_INTERVAL,
    staleTime: 30 * 1000,
  })
}

/**
 * Hook for device breakdown
 */
export function useDeviceBreakdown(dateRange: DateRange) {
  const accountId = useActiveAccountId()

  return useQuery<DeviceBreakdown | null>({
    queryKey: [
      'analytics',
      'devices',
      accountId,
      dateRange.start.toISOString(),
      dateRange.end.toISOString(),
    ],
    queryFn: async () => {
      if (!accountId) return null
      return getDeviceBreakdown(supabase, accountId, dateRange)
    },
    enabled: !!accountId,
    refetchInterval: REFRESH_INTERVAL,
    staleTime: 30 * 1000,
  })
}

/**
 * Hook for all analytics data at once (for initial load)
 */
export function useAllAnalytics(dateRange: DateRange) {
  const accountId = useActiveAccountId()

  return useQuery({
    queryKey: [
      'analytics',
      'all',
      accountId,
      dateRange.start.toISOString(),
      dateRange.end.toISOString(),
    ],
    queryFn: async () => {
      if (!accountId) return null

      // Fetch all data in parallel
      const [overview, traffic, revenue, topPages, funnel, ads, devices] =
        await Promise.all([
          getOverviewStats(supabase, accountId, dateRange),
          getDailyTraffic(supabase, accountId, dateRange),
          getDailyRevenue(supabase, accountId, dateRange),
          getTopPages(supabase, accountId, dateRange, 10),
          getBookingFunnel(supabase, accountId, dateRange),
          getAdPerformance(supabase, accountId, dateRange),
          getDeviceBreakdown(supabase, accountId, dateRange),
        ])

      return {
        overview,
        traffic,
        revenue,
        topPages,
        funnel,
        adPerformance: ads,
        devices,
        lastUpdated: new Date().toISOString(),
      }
    },
    enabled: !!accountId,
    refetchInterval: REFRESH_INTERVAL,
    staleTime: 30 * 1000,
  })
}
